import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { createUseStyles } from 'react-jss';
import moment from 'moment';
import {
  Button,
  ColorBadge,
  Loading,
  LogoutIcon,
  ManualIcon,
  MiniTooltip,
  Search,
  Theme,
} from '@spoiler-alert/ui-library';
import { UserSessionsQuery } from '../../graphql/queries';
import { EndSession, LockoutUser } from '../../graphql/mutations';
import { TitleService } from '../../services';
import { Breadcrumbs } from '../../store';

const useStyles = createUseStyles({
  table: {
    width: '100%',
    border: `1px solid ${Theme.borderColor}`,
    borderSpacing: 0,
    '& th, & td': {
      padding: [5, 15],
      height: 50,
    },
    '& th': {
      textAlign: 'left',
      borderBottom: `1px solid ${Theme.borderColor}`,
    },
    '& tbody>tr:nth-child(odd)>td': {
      background: Theme.grey5,
    },
  },
  buttons: {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
  },
  emptyButton: {
    width: 38,
    height: 36,
  },
  ipAddresses: {
    display: 'flex',
    gap: 4,
    flexWrap: 'wrap',
    '&>span': {
      display: 'block',
      height: 22,
    },
  },
  searchBox: {
    width: 300,
    marginBottom: 15,
  },
});

const UserSessionManager = () => {
  const classes = useStyles();
  const { data, loading } = useQuery(UserSessionsQuery);
  const [endSession] = useMutation(EndSession);
  const [lockoutUser] = useMutation(LockoutUser);

  const [searchText, setSearchText] = useState('');

  const sessions = useMemo(() => {
    if (!data) return [];
    return data.adminUserSessionsQuery.map((s) => {
      return {
        ...s,
        searchable: JSON.stringify(s).toLowerCase(),
      };
    });
  }, [data?.adminUserSessionsQuery]);

  const filteredSessions = useMemo(() => {
    if (searchText === '') return sessions;
    return sessions.filter((s) => s.searchable.includes(searchText));
  }, [sessions, searchText]);

  useEffect(() => {
    TitleService.setTitles('Active User Sessions');
    Breadcrumbs.set([
      {
        url: '/session-manager',
        title: 'Active User Sessions',
      },
    ]);
  }, []);

  const getUserTypeBadge = (userType) => {
    let label = 'User';
    let theme;
    switch (userType) {
      case 'ADMIN_USER':
        theme = 'teal';
        label = 'Admin';
        break;
      case 'USER':
        label = 'App User';
        theme = 'green';
        break;
      case 'BUYER_USER':
        label = 'Buyer';
        theme = 'orange';
        break;
      default:
        theme = 'grey';
    }
    return <ColorBadge theme={theme}>{label}</ColorBadge>;
  };

  const getValidityBadge = (isValid) => {
    const label = isValid ? 'Valid' : 'Blocked';
    const theme = isValid ? 'green' : 'red';
    return <ColorBadge theme={theme}>{label}</ColorBadge>;
  };

  const handleEndSession = (session) => {
    let message = 'Are you sure? This will log the user out of the platform.';
    if (session.userType === 'BUYER_USER')
      message = 'Are you sure? This buyer will need to be issued a new BuyerX link to regain access.';
    if (window.confirm(message)) endSession({ variables: { sessionId: session._id } });
  };

  const handleLockout = (session) => {
    if (window.confirm('Are you sure? This will prevent this user from logging in for 24 hours.'))
      lockoutUser({ variables: { userId: session.user._id, userType: session.userType } });
  };

  const getButtons = (session) => {
    const actions = [];
    if (session.isValid) {
      actions.push(
        <MiniTooltip text="End Session" key="end-session">
          <Button secondary icon={LogoutIcon} onClick={() => handleEndSession(session)} />
        </MiniTooltip>,
      );
    } else {
      actions.push(<div key="end-session" className={classes.emptyButton} />);
    }
    if (session.userType !== 'BUYER_USER') {
      actions.push(
        <MiniTooltip text="Lock User Out" key="lock-user">
          <Button warning icon={ManualIcon} onClick={() => handleLockout(session)} />
        </MiniTooltip>,
      );
    } else {
      actions.push(<div key="lock-user" className={classes.emptyButton} />);
    }
    return <div className={classes.buttons}>{actions}</div>;
  };

  const handleSearch = (_searchText) => {
    setSearchText(_searchText.toLowerCase());
  };

  return (
    <div>
      <Search className={classes.searchBox} placeholder="Search Sessions" onChange={handleSearch} />
      <Loading loading={loading}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Site</th>
              <th>Type</th>
              <th>Session Status</th>
              <th>Logged In</th>
              <th>Last Seen</th>
              <th>IPs</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSessions.map((s) => (
              <tr key={s._id}>
                <td>
                  <div>{s.user.fullName}</div>
                  <div>{s.user.email}</div>
                </td>
                <td>{s.siteName}</td>
                <td>{getUserTypeBadge(s.userType)}</td>
                <td>{getValidityBadge(s.isValid)}</td>
                <td>{moment(s.createdAt).fromNow()}</td>
                <td>{moment(s.lastUsedAt).fromNow()}</td>
                <td title={s.userAgent} className={classes.ipAddresses}>
                  {s.ipAddresses.map((ip) => (
                    <Fragment key={ip}>
                      <ColorBadge theme="grey">{ip}</ColorBadge>
                    </Fragment>
                  ))}
                </td>
                <td>{getButtons(s)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Loading>
    </div>
  );
};

UserSessionManager.propTypes = {
  user: PropTypes.object,
};

export default UserSessionManager;
